import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import LanguageIcon from '@mui/icons-material/Language';
import GavelIcon from '@mui/icons-material/Gavel';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ErrorIcon from '@mui/icons-material/Error';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// ---------------------------------------------------------------------------
// Constantes de cor
// ---------------------------------------------------------------------------

const CORES_CATEGORIA: Record<string, string> = {
  LEGISLACAO: '#1E3A5F',
  NORMA_TECNICA: '#2D7D9A',
  ALERTA_EPIDEMIOLOGICO: '#E74C3C',
  RUMOR: '#F39C12',
  NOTICIA: '#27AE60',
};

const CORES_RISCO: Record<string, string> = {
  CRITICO: '#E74C3C',
  ALTO: '#F39C12',
  MEDIO: '#F1C40F',
  BAIXO: '#27AE60',
};

const CORES_ABRANGENCIA: Record<string, string> = {
  INTERNACIONAL: '#8E44AD',
  NACIONAL: '#2980B9',
  ESTADUAL: '#27AE60',
  MUNICIPAL: '#F39C12',
};

const CORES_PIE_FALLBACK = [
  '#1E3A5F', '#2D7D9A', '#27AE60', '#F39C12', '#E74C3C',
  '#8E44AD', '#E67E22', '#16A085', '#2C3E50', '#D35400',
];

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface Kpis {
  total_documentos: number;
  total_legislacao: number;
  total_normas: number;
  total_alertas: number;
  total_rumores: number;
  total_noticias: number;
  total_criticos: number;
  total_altos: number;
  novos_7_dias: number;
  nao_lidos: number;
}

interface TimelineItem {
  semana_inicio: string;
  categoria: string;
  total: number;
}

interface DistribuicaoItem {
  name: string;
  value: number;
}

interface AlertaItem {
  id: number | string;
  titulo: string;
  resumo: string;
  url_original: string;
  fonte: string;
  categoria: string;
  abrangencia: string;
  nivel_risco: string;
  atividade: string;
  data_publicacao: string;
  processado?: boolean;
}

interface FonteColeta {
  id: number;
  nome: string;
  tipo: string;
  url_base: string;
  fonte: string;
  ultima_coleta: string;
  proxima_coleta: string;
  status_ultima: string;
  total_documentos_coletados: number;
}

interface NotificacaoItem {
  id: number;
  canal: string;
  destinatario: string;
  assunto: string;
  enviado_em: string;
  status: string;
  documento_titulo: string;
  nivel_risco: string;
}

interface DashboardData {
  kpis: Kpis;
  timeline: TimelineItem[];
  distribuicao: {
    por_categoria: DistribuicaoItem[];
    por_fonte: DistribuicaoItem[];
    por_risco: DistribuicaoItem[];
    por_abrangencia: DistribuicaoItem[];
    por_atividade: DistribuicaoItem[];
  };
  alertas: AlertaItem[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toISO = (d: Date): string => d.toISOString().split('T')[0];

/**
 * Pivota array flat [{semana_inicio, categoria, total}, ...]
 * para [{semana_inicio, LEGISLACAO: x, RUMOR: y, ...}, ...]
 */
function pivotTimeline(
  flat: TimelineItem[],
): { data: Record<string, any>[]; categorias: string[] } {
  const catSet = new Set<string>();
  const map = new Map<string, Record<string, any>>();
  for (const item of flat) {
    const semana = String(item.semana_inicio ?? '');
    catSet.add(String(item.categoria ?? ''));
    if (!map.has(semana)) map.set(semana, { semana_inicio: semana });
    map.get(semana)![String(item.categoria)] = Number(item.total ?? 0);
  }
  return { data: Array.from(map.values()), categorias: Array.from(catSet) };
}

/**
 * Formata label de categoria para exibicao:
 * ALERTA_EPIDEMIOLOGICO -> Alerta Epidemiologico
 */
function formatLabel(raw: string): string {
  if (!raw) return 'N/I';
  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Formata data ISO para dd/mm/aaaa
 */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const parts = iso.split('T')[0].split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/**
 * Retorna cor para uma categoria, com fallback por indice.
 */
function corCategoria(name: string, index: number): string {
  return (
    CORES_CATEGORIA[name] ||
    CORES_PIE_FALLBACK[index % CORES_PIE_FALLBACK.length]
  );
}

/**
 * Retorna cor para nivel de risco.
 */
function corRisco(nivel: string): string {
  const upper = (nivel ?? '').toUpperCase();
  return CORES_RISCO[upper] || '#9E9E9E';
}

/**
 * Retorna cor para abrangencia.
 */
function corAbrangencia(name: string, index: number): string {
  return (
    CORES_ABRANGENCIA[name] ||
    CORES_PIE_FALLBACK[index % CORES_PIE_FALLBACK.length]
  );
}

// ---------------------------------------------------------------------------
// Componente de Tooltip customizado para PieChart
// ---------------------------------------------------------------------------

const PieTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const { name, value } = payload[0];
  return (
    <Paper
      elevation={3}
      sx={{ px: 1.5, py: 1, borderRadius: 1, fontSize: '0.85rem' }}
    >
      <Typography variant="body2" fontWeight={600}>
        {formatLabel(name)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {Number(value).toLocaleString('pt-BR')} documento(s)
      </Typography>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Componente Principal
// ---------------------------------------------------------------------------

const MonitoramentoVisaWeb: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Datas padrao: ultimos 6 meses
  const hoje = new Date();
  const seisMesesAtras = new Date();
  seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6);

  const [dataInicio, setDataInicio] = useState(toISO(seisMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));
  const [fontesColeta, setFontesColeta] = useState<FonteColeta[]>([]);
  const [notificacoes, setNotificacoes] = useState<NotificacaoItem[]>([]);

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  // -------------------------------------------------------------------------
  // Fetch dados
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getVisaWebDashboard(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        if (!cancelled) {
          setData(result);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              'Erro ao carregar dados do monitoramento VISA Web.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    let cancelled = false;
    const loadExtras = async () => {
      try {
        const [fontes, notifs] = await Promise.all([
          monitoramentoService.getVisaWebFontesColeta(municipioAtivo.id),
          monitoramentoService.getVisaWebNotificacoes(municipioAtivo.id, 10),
        ]);
        if (!cancelled) {
          setFontesColeta(Array.isArray(fontes) ? fontes : []);
          setNotificacoes(Array.isArray(notifs) ? notifs : []);
        }
      } catch {
        if (!cancelled) {
          setFontesColeta([]);
          setNotificacoes([]);
        }
      }
    };
    loadExtras();
    return () => { cancelled = true; };
  }, [municipioAtivo?.id]);

  // -------------------------------------------------------------------------
  // Dados derivados / memoizados
  // -------------------------------------------------------------------------

  const kpis: Kpis = useMemo(
    () =>
      data?.kpis || {
        total_documentos: 0,
        total_legislacao: 0,
        total_normas: 0,
        total_alertas: 0,
        total_rumores: 0,
        total_noticias: 0,
        total_criticos: 0,
        total_altos: 0,
        novos_7_dias: 0,
        nao_lidos: 0,
      },
    [data],
  );

  const alertas: AlertaItem[] = useMemo(
    () => (data?.alertas || []).slice(0, 10),
    [data],
  );

  const porCategoria: DistribuicaoItem[] = useMemo(
    () =>
      (data?.distribuicao?.por_categoria || []).map((item) => ({
        name: item.name,
        value: Number(item.value ?? 0),
      })),
    [data],
  );

  const porFonte: DistribuicaoItem[] = useMemo(
    () =>
      (data?.distribuicao?.por_fonte || []).map((item) => ({
        name: item.name,
        value: Number(item.value ?? 0),
      })),
    [data],
  );

  const porAbrangencia: DistribuicaoItem[] = useMemo(
    () =>
      (data?.distribuicao?.por_abrangencia || []).map((item) => ({
        name: item.name,
        value: Number(item.value ?? 0),
      })),
    [data],
  );

  const porAtividade: DistribuicaoItem[] = useMemo(
    () =>
      (data?.distribuicao?.por_atividade || []).map((item) => ({
        name: item.name,
        value: Number(item.value ?? 0),
      })),
    [data],
  );

  const porRisco: DistribuicaoItem[] = useMemo(
    () =>
      (data?.distribuicao?.por_risco || []).map((item) => ({
        name: item.name,
        value: Number(item.value ?? 0),
      })),
    [data],
  );

  const { data: timelineData, categorias: timelineCategorias } = useMemo(
    () => pivotTimeline(data?.timeline || []),
    [data],
  );

  // -------------------------------------------------------------------------
  // Estado: sem municipio
  // -------------------------------------------------------------------------

  if (!municipioAtivo) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          px: 3,
        }}
      >
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar o monitoramento VISA Web.
        </Typography>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Estado: carregando
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton
          variant="rectangular"
          height={60}
          sx={{ borderRadius: 2, mt: 2, mb: 3 }}
        />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={2} key={i}>
              <Skeleton
                variant="rectangular"
                height={120}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton
          variant="rectangular"
          height={280}
          sx={{ borderRadius: 2, mb: 3 }}
        />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Skeleton
              variant="rectangular"
              height={350}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton
              variant="rectangular"
              height={350}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        </Grid>
        <Skeleton
          variant="rectangular"
          height={350}
          sx={{ borderRadius: 2, mb: 3 }}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Skeleton
              variant="rectangular"
              height={350}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton
              variant="rectangular"
              height={350}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Estado: erro
  // -------------------------------------------------------------------------

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Estado: dados vazios
  // -------------------------------------------------------------------------

  const hasData =
    data &&
    (kpis.total_documentos > 0 ||
      alertas.length > 0 ||
      porCategoria.length > 0 ||
      timelineData.length > 0);

  if (!hasData) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        {/* Header mesmo sem dados */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              VISA — Monitoramento Web
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {municipioAtivo.nome}
            </Typography>
          </Box>
          <DateRangeSelector
            dataInicio={dataInicio}
            dataFim={dataFim}
            onChange={handleDateChange}
          />
        </Box>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Nenhum dado encontrado para o periodo selecionado. Ajuste o intervalo
          de datas ou verifique se o monitoramento web esta configurado para
          este municipio.
        </Alert>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Render principal
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* ================================================================= */}
      {/* 1. Header                                                         */}
      {/* ================================================================= */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            VISA — Monitoramento Web
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* ================================================================= */}
      {/* 2. KPI Cards                                                      */}
      {/* ================================================================= */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            titulo="Total Documentos"
            valor={Number(kpis.total_documentos).toLocaleString('pt-BR')}
            icone={<LanguageIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            titulo="Legislacao"
            valor={Number(kpis.total_legislacao).toLocaleString('pt-BR')}
            icone={<GavelIcon />}
            cor="#2D7D9A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            titulo="Alertas Epidemiologicos"
            valor={Number(kpis.total_alertas).toLocaleString('pt-BR')}
            icone={<WarningIcon />}
            cor="#E74C3C"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            titulo="Rumores"
            valor={Number(kpis.total_rumores).toLocaleString('pt-BR')}
            icone={<TrendingUpIcon />}
            cor="#F39C12"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            titulo="Criticos"
            valor={Number(kpis.total_criticos).toLocaleString('pt-BR')}
            icone={<ErrorIcon />}
            cor="#E74C3C"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            titulo="Novos 7 dias"
            valor={Number(kpis.novos_7_dias).toLocaleString('pt-BR')}
            icone={<FiberNewIcon />}
            cor="#27AE60"
          />
        </Grid>
      </Grid>

      {/* ================================================================= */}
      {/* 3. Alertas Recentes                                               */}
      {/* ================================================================= */}
      {alertas.length > 0 && (
        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            mb: 3,
            borderLeft: '4px solid #E74C3C',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Alertas Recentes
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Titulo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Classificacao</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fonte</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Risco</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Link
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alertas.map((alerta) => (
                  <TableRow
                    key={alerta.id}
                    hover
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell
                      sx={{
                        maxWidth: 360,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={alerta.titulo}
                    >
                      {alerta.titulo}
                    </TableCell>
                    <TableCell>
                      {alerta.processado !== false && (
                        <Chip label="Auto" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {alerta.fonte || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatLabel(alerta.nivel_risco)}
                        size="small"
                        sx={{
                          backgroundColor: corRisco(alerta.nivel_risco),
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(alerta.data_publicacao)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {alerta.url_original ? (
                        <IconButton
                          size="small"
                          href={alerta.url_original}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: '#1E3A5F' }}
                          title="Abrir documento original"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {/* ================================================================= */}
      {/* 4. PieChart por Categoria + PieChart por Fonte                    */}
      {/* ================================================================= */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Por Categoria */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Distribuicao por Categoria
            </Typography>
            {porCategoria.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={porCategoria}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label={({ name, percent }: any) =>
                      `${formatLabel(name)}: ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {porCategoria.map((item, index) => (
                      <Cell
                        key={`cat-${index}`}
                        fill={corCategoria(item.name, index)}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend
                    formatter={(value: string) => formatLabel(value)}
                    wrapperStyle={{ fontSize: '0.8rem' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: 'center' }}
              >
                Sem dados de categoria para o periodo.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Por Fonte */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Distribuicao por Fonte
            </Typography>
            {porFonte.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={porFonte}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label={({ name, percent }: any) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {porFonte.map((_, index) => (
                      <Cell
                        key={`fonte-${index}`}
                        fill={
                          CORES_PIE_FALLBACK[index % CORES_PIE_FALLBACK.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: 'center' }}
              >
                Sem dados de fonte para o periodo.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ================================================================= */}
      {/* 5. BarChart Evolucao Semanal (stacked)                            */}
      {/* ================================================================= */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Evolucao Semanal por Categoria
        </Typography>
        {timelineData.length > 0 ? (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart
              data={timelineData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="semana_inicio"
                tick={{ fontSize: 11 }}
                tickFormatter={(v: string) => {
                  const parts = v.split('-');
                  if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
                  return v;
                }}
              />
              <YAxis
                tickFormatter={(v: number) => v.toLocaleString('pt-BR')}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  Number(value).toLocaleString('pt-BR'),
                  formatLabel(name),
                ]}
                labelFormatter={(label: any) => {
                  return `Semana: ${formatDate(label)}`;
                }}
              />
              <Legend formatter={(value: string) => formatLabel(value)} />
              {timelineCategorias.map((cat, idx) => (
                <Bar
                  key={cat}
                  dataKey={cat}
                  name={cat}
                  stackId="categorias"
                  fill={corCategoria(cat, idx)}
                  radius={
                    idx === timelineCategorias.length - 1
                      ? [4, 4, 0, 0]
                      : [0, 0, 0, 0]
                  }
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 6, textAlign: 'center' }}
          >
            Sem dados de evolucao semanal para o periodo.
          </Typography>
        )}
      </Paper>

      {/* ================================================================= */}
      {/* 6. BarChart por Abrangencia + PieChart por Atividade              */}
      {/* ================================================================= */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* BarChart por Abrangencia */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Distribuicao por Abrangencia
            </Typography>
            {porAbrangencia.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={porAbrangencia}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={(v: number) => v.toLocaleString('pt-BR')}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 12 }}
                    width={120}
                    tickFormatter={(v: string) => formatLabel(v)}
                  />
                  <Tooltip
                    formatter={(value: any) => [
                      Number(value).toLocaleString('pt-BR'),
                      'Documentos',
                    ]}
                    labelFormatter={(label: any) => formatLabel(label)}
                  />
                  <Bar dataKey="value" name="Documentos" radius={[0, 4, 4, 0]}>
                    {porAbrangencia.map((item, index) => (
                      <Cell
                        key={`abr-${index}`}
                        fill={corAbrangencia(item.name, index)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: 'center' }}
              >
                Sem dados de abrangencia para o periodo.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* PieChart por Atividade */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Distribuicao por Atividade
            </Typography>
            {porAtividade.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={porAtividade}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label={({ name, percent }: any) =>
                      `${formatLabel(name)}: ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {porAtividade.map((_, index) => (
                      <Cell
                        key={`ativ-${index}`}
                        fill={
                          CORES_PIE_FALLBACK[index % CORES_PIE_FALLBACK.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend
                    formatter={(value: string) => formatLabel(value)}
                    wrapperStyle={{ fontSize: '0.8rem' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: 'center' }}
              >
                Sem dados de atividade para o periodo.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ================================================================= */}
      {/* 6b. Distribuicao por Risco                                        */}
      {/* ================================================================= */}
      {porRisco.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Distribuicao por Nivel de Risco
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={porRisco} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tickFormatter={(v: string) => formatLabel(v)} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(v: any) => [Number(v).toLocaleString('pt-BR'), 'Documentos']} />
              <Bar dataKey="value" name="Documentos" radius={[4, 4, 0, 0]}>
                {porRisco.map((item) => (
                  <Cell key={item.name} fill={corRisco(item.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* ================================================================= */}
      {/* 6c. Fontes monitoradas + Notificacoes                             */}
      {/* ================================================================= */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Fontes Monitoradas (Coleta Automatizada)
            </Typography>
            {fontesColeta.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Fonte</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Ultima coleta</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fontesColeta.map((f) => (
                      <TableRow key={f.id} hover>
                        <TableCell>{f.nome}</TableCell>
                        <TableCell>{formatLabel(f.tipo)}</TableCell>
                        <TableCell>{f.ultima_coleta || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={f.status_ultima}
                            size="small"
                            color={f.status_ultima === 'OK' ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" href={f.url_base} target="_blank" rel="noopener noreferrer">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhuma fonte configurada.</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Notificacoes Automaticas Recentes
            </Typography>
            {notificacoes.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {notificacoes.map((n) => (
                  <Box key={n.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                      <Chip label={n.canal} size="small" variant="outlined" />
                      {n.nivel_risco && (
                        <Chip label={formatLabel(n.nivel_risco)} size="small" sx={{ bgcolor: corRisco(n.nivel_risco), color: '#fff' }} />
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={600} noWrap title={n.assunto}>
                      {n.assunto}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.enviado_em} {n.destinatario ? `| ${n.destinatario}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhuma notificacao recente.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ================================================================= */}
      {/* 7. Botao "Ver Todos os Documentos"                                */}
      {/* ================================================================= */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/monitoramento/visa-web/detalhes')}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            backgroundColor: '#1E3A5F',
            '&:hover': {
              backgroundColor: '#16304D',
            },
          }}
        >
          Ver Todos os Documentos
        </Button>
      </Box>

      {/* ================================================================= */}
      {/* 8. Analise Preditiva + Chat IA                                    */}
      {/* ================================================================= */}
      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="visa-web"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoVisaWeb;
